import React, { useEffect, useMemo, useRef } from 'react';
import { playSoundEffect } from '../hooks/useSoundEffects';
import './LegendaryDeployVfx.css';

export const LEGENDARY_UNIQUE_DEPLOY_SOUND_PATH = '/sounds/legendary_deploy.mp3';
export const LEGENDARY_DEPLOY_SOUND_PATH = '/sounds/legendary_deploy_4.mp3';
/** Lower gain for unique legendary deploy SFX so unit voice lines remain clear in the mix. */
export const LEGENDARY_UNIQUE_DEPLOY_SOUND_VOLUME = 0.35;
/** Default deploy SFX volume for non-unique legendary deployments. */
export const LEGENDARY_DEPLOY_SOUND_VOLUME = 0.5;

/**
 * Classify whether an order should use legendary glow, unique ray, and which deploy sound.
 * Matches BattleUI deploy rules, including availability=created (e.g. Shadow Sniper).
 * @param {object} order
 */
export function classifyLegendaryDeployVfx(order) {
  if (!order) {
    throw new Error('classifyLegendaryDeployVfx: order is required');
  }
  const rarity = (order.rarity ?? '').toString().toLowerCase();
  const isUnique = order.is_unique === true || order.isUnique === true;
  const availability = (order.availability ?? '').toString().trim().toLowerCase();
  const isAvailabilityCreated = availability === 'created';
  const isLegendary = rarity === 'legendary';
  const isLegendaryUnique = isLegendary && isUnique && !isAvailabilityCreated;
  return {
    isLegendary,
    isLegendaryUnique,
    showGlow: isLegendary && (!isUnique || isAvailabilityCreated),
    showUniqueRay: isLegendaryUnique,
  };
}

function getLegendaryRevealIdentity(order) {
  if (!order) {
    throw new Error('getLegendaryRevealIdentity: order is required');
  }
  const instanceId = order.instanceId != null ? String(order.instanceId) : '';
  const id = order.id != null ? String(order.id) : '';
  if (!instanceId && !id) {
    throw new Error('getLegendaryRevealIdentity: order.instanceId or order.id is required');
  }
  return `${instanceId || id}:${(order.rarity ?? '').toString().toLowerCase()}:${order.is_unique === true || order.isUnique === true}:${(order.availability ?? '').toString()}`;
}

/**
 * Play the BattleUI legendary deploy sound for an order (glow vs unique ray).
 * @returns {function} cleanup that cancels a pending delay
 */
export function playLegendaryDeploySound(order, delayMs = 0) {
  const { isLegendary, isLegendaryUnique } = classifyLegendaryDeployVfx(order);
  if (!isLegendary) {
    return () => {};
  }
  const soundPath = isLegendaryUnique
    ? LEGENDARY_UNIQUE_DEPLOY_SOUND_PATH
    : LEGENDARY_DEPLOY_SOUND_PATH;
  const volume = isLegendaryUnique
    ? LEGENDARY_UNIQUE_DEPLOY_SOUND_VOLUME
    : LEGENDARY_DEPLOY_SOUND_VOLUME;
  const timeoutId = setTimeout(() => {
    playSoundEffect(soundPath, volume);
  }, delayMs);
  return () => clearTimeout(timeoutId);
}

/** Legendary-unique deploy ray with sparkling orbs (e.g. Samuel Johnson). Orbs randomly distributed throughout the ray; sibling so not blurred. */
export const LegendaryUniqueDeployRay = () => (
  <>
    <div className="legendary-unique-deploy-ray" aria-hidden="true" />
    <div className="legendary-unique-deploy-ray-orbs" aria-hidden="true">
      {Array.from({ length: 126 }, (_, i) => (
        <span
          key={i}
          className="legendary-deploy-glow-orb"
          style={{
            left: `${12 + (i * 13) % 76}%`,
            top: `${4 + (i * 19) % 92}%`,
            animationDelay: `${(i * 0.07) % 2}s`,
          }}
        />
      ))}
    </div>
  </>
);

/** Legendary deploy glow with flickering orbs (non-unique legendaries e.g. t-rex). Orbs are a sibling layer so they are not blurred by the glow's filter. */
export const LegendaryDeployGlow = () => (
  <>
    <div className="legendary-deploy-glow" aria-hidden="true" />
    <div className="legendary-deploy-glow-orbs" aria-hidden="true">
      {Array.from({ length: 42 }, (_, i) => (
        <span
          key={i}
          className="legendary-deploy-glow-orb"
          style={{
            left: `${8 + (i * 11) % 84}%`,
            top: `${8 + (i * 17) % 84}%`,
            animationDelay: `${(i * 0.08) % 2}s`,
          }}
        />
      ))}
    </div>
  </>
);

/**
 * Play legendary deploy SFX and show glow/unique-ray when a legendary order is revealed
 * (mulligan, skeleton crew). Animation matches BattleUI deploy VFX.
 * Must be a direct child of a position:relative; overflow:visible card wrapper,
 * with the order art in a sibling at z-index 1 (same structure as MulliganDialog).
 */
export function LegendaryOrderRevealVfx({
  order,
  soundDelayMs = 0,
  playSound = true,
}) {
  if (!order) {
    throw new Error('LegendaryOrderRevealVfx: order is required');
  }

  const vfx = useMemo(() => classifyLegendaryDeployVfx(order), [
    order.id,
    order.instanceId,
    order.rarity,
    order.is_unique,
    order.isUnique,
    order.availability,
  ]);
  const revealIdentity = getLegendaryRevealIdentity(order);
  const orderRef = useRef(order);
  orderRef.current = order;

  useEffect(() => {
    if (!playSound || !vfx.isLegendary) {
      return undefined;
    }
    return playLegendaryDeploySound(orderRef.current, soundDelayMs);
  }, [playSound, revealIdentity, soundDelayMs, vfx.isLegendary]);

  if (!vfx.isLegendary) {
    return null;
  }

  return (
    <div className="legendary-order-reveal-vfx" aria-hidden="true">
      {vfx.showUniqueRay && <LegendaryUniqueDeployRay />}
      {vfx.showGlow && <LegendaryDeployGlow />}
    </div>
  );
}

export default LegendaryOrderRevealVfx;
